import React, { useState } from 'react';
import { addMember, removeMember } from '../services/api';

const OrganizationManager = ({ organizations, onRefresh }) => {
  const [showMemberForm, setShowMemberForm] = useState(null);
  const [memberData, setMemberData] = useState({
    username: '',
    role: 'member',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const resetMemberForm = () => {
    setMemberData({
      username: '',
      role: 'member',
    });
    setShowMemberForm(null);
    setErrors({});
  };

  const handleMemberChange = e => {
    setMemberData({
      ...memberData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddMember = async e => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await addMember(showMemberForm.id, memberData);
      resetMemberForm();
      onRefresh();
    } catch (error) {
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (orgId, userId) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      try {
        await removeMember(orgId, userId);
        onRefresh();
      } catch (error) {
        alert('Error removing member. Please try again.');
      }
    }
  };

  return (
    <div className="organization-manager">
      <div className="manager-header">
        <h2>Organizations</h2>
      </div>

      {showMemberForm && (
        <div className="form-modal">
          <div className="form-card">
            <div className="form-header">
              <h3>Add Member to {showMemberForm.name}</h3>
              <button onClick={resetMemberForm} className="close-button">
                ×
              </button>
            </div>

            {errors.general && (
              <div className="error-message">{errors.general}</div>
            )}
            {errors.error && (
              <div className="error-message">{errors.error}</div>
            )}

            <form onSubmit={handleAddMember}>
              <div className="form-group">
                <label htmlFor="username">Username:</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={memberData.username}
                  onChange={handleMemberChange}
                  required
                />
                {errors.username && (
                  <span className="field-error">
                    {errors.username.join(', ')}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="role">Role:</label>
                <select
                  id="role"
                  name="role"
                  value={memberData.role}
                  onChange={handleMemberChange}
                >
                  <option value="member">Member</option>
                  <option value="admin">Administrator</option>
                  <option value="owner">Owner</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={resetMemberForm}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="organizations-list">
        {Array.isArray(organizations?.results) &&
        organizations.results.length === 0 ? (
          <div className="empty-state">
            <p>
              No organizations found. Contact an administrator to join an
              organization.
            </p>
          </div>
        ) : (
          (Array.isArray(organizations?.results)
            ? organizations.results
            : []
          ).map(org => (
            <div key={org.id} className="organization-card">
              <div className="org-header">
                <h3>{org.name}</h3>
                <div className="org-actions">
                  <button
                    onClick={() => setShowMemberForm(org)}
                    className="add-member-button"
                  >
                    Add Member
                  </button>
                </div>
              </div>

              <p>{org.description}</p>

              <div className="org-meta">
                <p>Created: {new Date(org.created_at).toLocaleDateString()}</p>
                <p>Members: {org.member_count}</p>
              </div>

              <div className="members-section">
                <h4>Members:</h4>
                {org.memberships.length === 0 ? (
                  <p className="no-members">No members</p>
                ) : (
                  <div className="members-list">
                    {org.memberships.map(membership => (
                      <div key={membership.id} className="member-item">
                        <div className="member-info">
                          <strong>{membership.user.username}</strong>
                          <span className={`role-badge ${membership.role}`}>
                            {membership.role}
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveMember(org.id, membership.user.id)
                          }
                          className="remove-member-button"
                          title="Remove member"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrganizationManager;
